import React, { useEffect, useState } from "react";
import { buildServiceUrl } from "../../utils/urlBuilder";
import { useTranslation } from "react-i18next";
import BreadCrumb from "../../components/HeaderComponents/BreadCrum";
import FilterSearch from "../../components/Filtering/FilterSearch";
import SearchButton from "../../components/buttons/SearchButton";
import FilterRightBox from "../../components/Filtering/FilterRightBoxOffering";
import DataTable from "../../components/DataTable/DataTable";
import { useFilteredData } from "../../hooks/useFilteredData";
import { useTableControls } from "../../hooks/useTableControls";
import axios from "axios";
import { useAuthRoles } from "../../hooks/useAuthRoles";
import ActivateUserForm from "./AdminUtils/ActivateUserForm";
import { Popover } from "../../components/DataTable/Popover";
import Tabs from "../../components/DataTable/Tabs";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import ConfirmDialog from "../../components/DataTable/ConfirmDialog";
import { toast } from "react-toastify";

const RegisterControl = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("A");
  const { dialogOpen, dialogConfig, openDialog, closeDialog } =
    useConfirmDialog();
  const {
    tableData,
    setTableData,
    searchTerm,
    pendingSearchTerm,
    filters,
    open,
    pagination,
    setPagination,
    rowCount,
    setRowCount,
    handleSearch,
    handleClearSearch,
    handleSearchSubmit,
    handleSearchKeyDown,
    handleClose,
    handleApplyFilters,
  } = useTableControls([]);
  const [openPetition, setOpenPetition] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const { isAdmin, token } = useAuthRoles();

  const handleOpenPetition = (row) => {
    setSelectedUser(row);
    setOpenPetition(true);
  };

  async function getTotalUserCount(token) {
    try {
      const res = await axios.get(
        `${buildServiceUrl("keycloak", "admin/realms/DSB/users?count=true")}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching user count:", error);
      return 0;
    }
  }

  const tableConfigs = {
    A: {
      label: t("register.pendingUsers"),
      fetcher: listPendingUsers,
    },
    B: {
      label: t("register.deletedUsers"),
      fetcher: async (paginationArg = pagination) => {
        const userToken = token;
        const first = (paginationArg.pageIndex - 1) * paginationArg.pageSize;
        const max = paginationArg.pageSize;
        const res = await axios.get(
          `${buildServiceUrl(
            "keycloak",
            `admin/realms/DSB/users?first=${first}&max=${max}`
          )}`,
          { headers: { Authorization: `Bearer ${userToken}` } }
        );

        const users = res.data;
        const deletedUsers = users.filter(
          (u) => u.attributes?.deleted?.[0] === "true"
        );

        return deletedUsers;
      },
    },
  };

  const columns = [
    {
      header: t("usermanagement.name"),
      accessorKey: "firstName",
      meta: { width: "15%" },
      cell: ({ getValue }) => (
        <div className="centered-cell">
          <span className="bold">{getValue()}</span>
        </div>
      ),
    },
    {
      header: t("register.surname"),
      accessorKey: "lastName",
      meta: { width: "25%" },
      cell: ({ getValue }) => (
        <div className="centered-cell">
          <span className="bold">{getValue()}</span>
        </div>
      ),
    },
    {
      header: t("register.email"),
      accessorKey: "email",
      meta: { width: "20%" },
    },
    {
      header: t("register.organization"),
      accessorKey: "attributes.organization",
      meta: { width: "25%" },
    },
    {
      header: t("profilePage.creation"),
      accessorKey: "createdTimestamp",
      meta: { width: "20%" },
      cell: ({ getValue }) => {
        const date = new Date(getValue());
        return date.toLocaleString();
      },
    },
  ];
  const currentConfig = tableConfigs[activeTab];
  const filteredData = useFilteredData(
    tableData,
    searchTerm,
    filters,
    currentConfig.columns
  );

  const handleDeleteUser = (user) => {
    openDialog({
      title: t("confirmDialog.deleteTitle"),
      message: t("confirmDialog.deleteMessage", { username: user.username }),
      onConfirm: async () => {
        await deleteUser(user.id);
        await refreshTableData(activeTab, pagination);
      },
    });
  };

  async function refreshTableData(tab = activeTab, paginationArg = pagination) {
    const totalCount = await getTotalUserCount(token);
    setRowCount(totalCount);

    const updated = await tableConfigs[tab].fetcher(paginationArg);
    setTableData(updated);
  }

  async function deleteUser(userId) {
    const userToken = token;
    try {
      await axios.delete(
        `${buildServiceUrl("keycloak", `admin/realms/DSB/users/${userId}`)}`,
        { headers: { Authorization: `Bearer ${userToken}` } }
      );
      toast.success(t("toaster.userDeleted"));
    } catch (error) {
      toast.error(t("toaster.userError"));
    }
  }

  async function listPendingUsers() {
    const userToken = token;

    const first = (pagination.pageIndex - 1) * pagination.pageSize;
    const max = pagination.pageSize;
    const res = await axios.get(
      `${buildServiceUrl(
        "keycloak",
        `admin/realms/DSB/users?first=${first}&max=${max}`
      )}`,
      { headers: { Authorization: `Bearer ${userToken}` } }
    );

    const users = res.data;

    const pendingUsers = await Promise.all(
      users
        .filter((u) => u.enabled === false)
        .map(async (user) => {
          try {
            const rolesRes = await axios.get(
              `${buildServiceUrl(
                "keycloak",
                `admin/realms/DSB/users/${user.id}/role-mappings/realm`
              )}`,
              { headers: { Authorization: `Bearer ${userToken}` } }
            );

            const roles = rolesRes.data
              .map((r) => r.name)
              .filter((r) => r !== "default-roles-dsb");

            return roles.length === 0 ? user : null;
          } catch (err) {
            console.error(
              `Error fetching roles for user ${user.username}:`,
              err
            );
            return null;
          }
        })
    );

    return pendingUsers.filter((u) => u !== null);
  }

  const getTableActions = () => {
    if (activeTab === "A") {
      return {
        onOpen: handleOpenPetition,
        showActions: isAdmin,
      };
    }

    if (activeTab === "B") {
      return {
        onDeleteUser: handleDeleteUser,
        showActions: isAdmin,
      };
    }

    return {};
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await refreshTableData(activeTab, pagination);
      } catch (err) {
        console.error("Error loading tab data:", err);
        setTableData([]);
      }
    };

    if (token) {
      fetchData();
    }
  }, [activeTab, token, pagination.pageIndex, pagination.pageSize]);

  return (
    <section className="pb4">
      <div className="w100 flexbread">
        <div className="divbreadcrum w90">
          <BreadCrumb />
        </div>
      </div>
      <div className="container containerflex">
        <div key={t.language} className="w90 labeltittle">
          <h2 className="tittlecatalog">{t("register.title")}</h2>
        </div>
        <div key={t.language} className="w86 filteractions pt3">
          <FilterSearch
            value={pendingSearchTerm}
            onChange={handleSearch}
            onClear={handleClearSearch}
            onKeyDown={handleSearchKeyDown}
          />
          <div className="plr2">
            <SearchButton onClick={handleSearchSubmit} />
          </div>
        </div>
        <FilterRightBox
          open={open}
          onClose={handleClose}
          onApplyFilters={handleApplyFilters}
        />
        <Tabs
          tabs={tableConfigs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
        <div className="w90 bgwhite mtb2">
          <div className="plr3 ptb">
            <DataTable
              columns={columns}
              data={filteredData}
              showActions={isAdmin}
              {...getTableActions()}
              pagination={pagination}
              setPagination={setPagination}
              rowCount={rowCount}
            />
            <ConfirmDialog
              open={dialogOpen}
              title={dialogConfig.title}
              message={dialogConfig.message}
              confirmText={dialogConfig.confirmText}
              cancelText={dialogConfig.cancelText}
              onClose={closeDialog}
              onConfirm={async () => {
                if (dialogConfig.onConfirm) await dialogConfig.onConfirm();
                closeDialog();
              }}
            />
          </div>
        </div>
      </div>
      {openPetition && selectedUser ? (
        <Popover open={openPetition} onClose={() => setOpenPetition(false)}>
          <ActivateUserForm
            userInfo={selectedUser}
            closePopover={() => setOpenPetition(false)}
            onUpdateTable={listPendingUsers}
            setTableData={setTableData}
          />
        </Popover>
      ) : null}
    </section>
  );
};

export default RegisterControl;
